import React, { useEffect, useLayoutEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setTopNavNameTitle } from '../../redux/features/mobileLayout/slice';
import { useAppDispatch } from '../../redux/store';
import { OpenShiftHistoryCard } from '../../components/OpenShifts';
import { getMyClaimShiftRequestHistory, cancelOpenShiftRequest } from '../../redux/features/openShift';
import { RootState } from '../../redux/store';
import DateTimeUtil from '../../utils/dateTime';

const OpenShiftsHistory: React.FC = () => {
  const dispatch = useAppDispatch();
  
  // Get claim shift request history from Redux store
  const { claimShiftRequestHistory, isLoadingClaimShiftRequestHistory, error } = useSelector((state: RootState) => state.openShift);

  useLayoutEffect(() => {
    dispatch(setTopNavNameTitle("Open Shifts History"));
  }, []);

  // Fetch claim shift request history when component mounts
  useEffect(() => {
    dispatch(getMyClaimShiftRequestHistory());
  }, [dispatch]);

  // Handle cancel shift request
  const handleCancelShiftRequest = async (shiftId: string) => {
    try {
      await dispatch(cancelOpenShiftRequest(shiftId)).unwrap();
      toast.success("Shift request cancelled successfully!");
      
      // Refresh the history data
      dispatch(getMyClaimShiftRequestHistory());
      
    } catch (error: any) {
      toast.error(error.message || "Failed to cancel shift request. Please try again.");
    }
  };

  // Helper function to format date using DateTimeUtil
  const formatDate = (date?: string) => {
    if (!date) return "No date";
    return DateTimeUtil.formatFullDate(new Date(date));
  };

  // Helper function to format time using DateTimeUtil
  const formatTime = (startTime?: string, endTime?: string) => {
    if (!startTime || !endTime) return "No time specified";
    
    // Convert time strings like "09:00:00" to readable format
    const formatTimeString = (timeStr: string) => {
      try {
        // Parse time string and format it
        const [hours, minutes] = timeStr.split(':');
        const hour = parseInt(hours, 10);
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        return `${displayHour}:${minutes} ${ampm}`;
      } catch {
        return timeStr; // Fallback to original string if parsing fails
      }
    };
    
    return `${formatTimeString(startTime)} – ${formatTimeString(endTime)}`;
  };

  // Helper function to get status from OpenShift data
  const getStatusFromOpenShift = (shift: any): 'pending' | 'approved' | 'rejected' => {
    // Map the status from the API response to our UI status
    const apiStatus = shift.status?.toLowerCase();
    if (apiStatus === 'approved') return 'approved';
    if (apiStatus === 'rejected') return 'rejected';
    return 'pending'; // Default to pending for any other status
  };

  return (
    <div className="min-h-screen bg-white px-5 pt-6 pb-10 max-w-screen-sm mx-auto">
      <section className="mt-1">
        <h2 className="text-[22px] leading-[28px] font-normal text-gray-800">Shift Request History</h2>
        
        {/* Loading State */}
        {isLoadingClaimShiftRequestHistory && (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-600">Error loading shift history: {error}</p>
          </div>
        )}

        {/* History Cards */}
        {!isLoadingClaimShiftRequestHistory && !error && (
          <>
            {claimShiftRequestHistory && claimShiftRequestHistory.length > 0 ? (
              claimShiftRequestHistory.map((shift) => (
                <OpenShiftHistoryCard
                  key={shift.os_sch_open_shift_transactions}
                  id={shift.os_sch_open_shift_transactions || ''}
                  location={shift.shift_details?.shift_name || 'Unnamed Shift'}
                  date={formatDate(shift.shift_date)}
                  time={shift.shift_details?.shift_start_time && shift.shift_details?.shift_end_time ? 
                    formatTime(shift.shift_details.shift_start_time, shift.shift_details.shift_end_time) : 
                    undefined}
                  status={getStatusFromOpenShift(shift)}
                  onCancel={handleCancelShiftRequest}
                />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                No shift request history found.
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
};

export default OpenShiftsHistory;
